import React, { memo, useMemo } from 'react';
import { Flex, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { pathKeys } from 'shared/lib/react-router';
import { BackButton } from 'shared/ui/BackButton';
import { AccountAndProjectTreeSelect } from '../AccountAndProjectTreeSelect';
import { EmployeeTreeSelect } from '../EmployeeTreeSelect';
import { TimesheetColumnsDropdown } from '../TimesheetColumnsDropdown';
import { TimesheetRangePicker } from '../TimesheetRangePicker';

import styles from './TimesheetsTableHeader.module.scss';

interface TimesheetsTableHeaderProps {
  columns: { key: string; label: string }[];
  columnsVisibilityMap: Record<string, boolean>;
  setColumnsVisibilityMap: (keys: Record<string, boolean>) => void;
  pinnedColumns: Record<string, boolean>;
  setPinnedColumns: (keys: Record<string, boolean>) => void;
}

const TimesheetsTableHeader = ({
  columns,
  columnsVisibilityMap,
  setColumnsVisibilityMap,
  pinnedColumns,
  setPinnedColumns,
}: TimesheetsTableHeaderProps): React.ReactElement => {
  const { t } = useTranslation('Timesheet');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const accountId = searchParams.get('accountId');
  const projectId = searchParams.get('projectId');
  const hasAccountOrProjectParams = Boolean(accountId || projectId);

  const backLink = useMemo(() => {
    if (accountId && projectId) {
      return pathKeys.project.root({ accountId });
    }
    if (accountId) {
      return pathKeys.account.root();
    }
    return null;
  }, [accountId, projectId]);

  return (
    <div className={styles.container}>
      <Flex gap={8} align="center">
        {hasAccountOrProjectParams && backLink && <BackButton onClick={() => navigate(backLink)} />}
        <Typography.Title level={5} className={styles.title}>
          {t('timesheet.title')}
        </Typography.Title>
      </Flex>
      <div className={styles.filters}>
        <TimesheetRangePicker />
        <div className={styles.selectionsBlock}>
          <AccountAndProjectTreeSelect disabled={hasAccountOrProjectParams} />
          <EmployeeTreeSelect />
        </div>
        <TimesheetColumnsDropdown
          columns={columns}
          columnsVisibilityMap={columnsVisibilityMap}
          setColumnsVisibilityMap={setColumnsVisibilityMap}
          pinnedColumns={pinnedColumns}
          setPinnedColumns={setPinnedColumns}
        />
      </div>
    </div>
  );
};

export default memo(TimesheetsTableHeader);
